import { useEffect, useRef, useState } from "react";
import { Camera, VideoCamera, Plus, Trash, X, Image as ImageIcon, FloppyDisk, ChatCircleText, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

// Resize/compress an image File to a JPEG data URL ≤ targetBytes
async function compressImage(file, targetBytes = 2_200_000, maxDim = 1600, mime = "image/jpeg") {
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = URL.createObjectURL(file);
  });
  let { width, height } = img;
  if (Math.max(width, height) > maxDim) {
    const k = maxDim / Math.max(width, height);
    width = Math.round(width * k);
    height = Math.round(height * k);
  }
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, width, height);
  let quality = 0.85;
  let dataUrl = canvas.toDataURL(mime, quality);
  while (dataUrl.length > targetBytes && quality > 0.4) {
    quality -= 0.1;
    dataUrl = canvas.toDataURL(mime, quality);
  }
  URL.revokeObjectURL(img.src);
  return dataUrl;
}

async function fileToDataUrl(file) {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function formatDate(iso) {
  try { return new Date(iso).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" }); }
  catch (_) { return iso?.slice(0, 10); }
}

export default function ClientProgress() {
  const [entries, setEntries] = useState([]);
  const [weeksSince, setWeeksSince] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [openEntryId, setOpenEntryId] = useState(null);
  const [openEntry, setOpenEntry] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/progress/entries");
      setEntries(data.entries || []);
      setWeeksSince(data.weeks_since_last_upload);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load progress");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openEntry_ = async (id) => {
    setOpenEntryId(id);
    setOpenEntry(null);
    try {
      const { data } = await api.get(`/progress/entries/${id}`);
      setOpenEntry(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load entry");
      setOpenEntryId(null);
    }
  };

  return (
    <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-8" data-testid="client-progress-page">
      <header className="flex items-start justify-between gap-4 mb-8">
        <div>
          <p className="tracker text-zinc-500 text-[10px] mb-2">[ PROGRESS PORTFOLIO ]</p>
          <h1 className="font-display text-4xl sm:text-5xl">Your timeline.</h1>
          <p className="text-zinc-400 mt-2 text-sm">
            Weekly photos + short videos so {`{`}your coach{`}`} can review form and track changes over time.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowNew(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-brand-light text-zinc-950 font-display text-xs tracking-wide hover:opacity-90 transition-opacity"
          data-testid="progress-new-entry-btn"
        >
          <Plus size={16} weight="bold" /> New entry
        </button>
      </header>

      {weeksSince !== null && (
        <div className="border border-zinc-800 bg-zinc-900/40 p-4 mb-6" data-testid="progress-weeks-since-tile">
          <p className="tracker text-zinc-500 text-[10px] mb-1">[ LAST UPLOAD ]</p>
          <p className="text-zinc-200 text-sm">
            <span className="font-display text-3xl text-brand-light mr-2">{weeksSince}</span>
            week{weeksSince === 1 ? "" : "s"} since your last upload
            {weeksSince >= 2 && <span className="text-amber-400 ml-2">— time for a check-in?</span>}
          </p>
        </div>
      )}

      {loading ? (
        <p className="tracker text-zinc-500 text-[11px]">Loading…</p>
      ) : entries.length === 0 ? (
        <div className="border border-dashed border-zinc-800 p-12 text-center" data-testid="progress-empty-state">
          <ImageIcon size={48} weight="duotone" className="text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400 mb-2">No entries yet.</p>
          <p className="text-zinc-500 text-sm">Start your portfolio with your first check-in photos + a short form-check video.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="progress-entries-grid">
          {entries.map((e) => (
            <button
              key={e.id}
              type="button"
              onClick={() => openEntry_(e.id)}
              className="text-left border border-zinc-800 bg-zinc-950 p-4 hover:border-zinc-700 transition-colors group"
              data-testid={`progress-entry-card-${e.id}`}
            >
              <div className="flex items-center justify-between gap-2 mb-3">
                <span className="tracker text-zinc-500 text-[10px]">{e.week_label}</span>
                <span className="text-zinc-600 text-[10px]">{formatDate(e.created_at)}</span>
              </div>
              <div className="aspect-[3/2] bg-zinc-900 border border-zinc-800 mb-3 flex items-center justify-center text-zinc-600">
                <div className="text-center">
                  <Camera size={32} weight="duotone" className="mx-auto" />
                  <p className="tracker text-[10px] mt-1">tap to view</p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-zinc-400 text-xs">
                <span className="inline-flex items-center gap-1"><Camera size={12} weight="bold" /> {e.photo_count}</span>
                {e.has_video && <span className="inline-flex items-center gap-1"><VideoCamera size={12} weight="bold" /> 1 video</span>}
                {e.comment_count > 0 && <span className="inline-flex items-center gap-1 text-brand-light"><ChatCircleText size={12} weight="bold" /> {e.comment_count}</span>}
              </div>
              {e.notes && <p className="text-zinc-500 text-xs mt-2 line-clamp-2">{e.notes}</p>}
            </button>
          ))}
        </div>
      )}

      {showNew && <NewEntryModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
      {openEntryId && (
        <EntryDetailModal
          entryId={openEntryId}
          entry={openEntry}
          onClose={() => { setOpenEntryId(null); setOpenEntry(null); }}
          onDeleted={() => { setOpenEntryId(null); setOpenEntry(null); load(); }}
          onReload={() => openEntry_(openEntryId)}
        />
      )}
    </div>
  );
}

function NewEntryModal({ onClose, onCreated }) {
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState([]); // [{ file, dataUrl, status }]
  const [video, setVideo] = useState(null);
  const [saving, setSaving] = useState(false);
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const onPickPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const room = 4 - photos.length;
    if (room <= 0) {
      toast.error("Maximum 4 photos per entry");
      return;
    }
    const toAdd = files.slice(0, room);
    const compressed = [];
    for (const f of toAdd) {
      try {
        const dataUrl = await compressImage(f);
        if (dataUrl.length > 3_000_000) {
          toast.error(`${f.name}: still too large after compression`);
          continue;
        }
        compressed.push({ name: f.name, dataUrl });
      } catch (err) {
        toast.error(`Couldn't process ${f.name}`);
      }
    }
    setPhotos((p) => [...p, ...compressed]);
  };

  const onPickVideo = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 11_000_000) {
      toast.error("Video too large — keep under 10MB (try a shorter clip or lower resolution)");
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(f);
      if (dataUrl.length > 14_000_000) {
        toast.error("Video too large after encoding — keep under 10MB raw");
        return;
      }
      setVideo({ name: f.name, dataUrl, size: f.size });
    } catch (_) {
      toast.error("Couldn't read video file");
    }
  };

  const save = async () => {
    if (!photos.length && !video) {
      toast.error("Add at least one photo or video");
      return;
    }
    setSaving(true);
    try {
      // Step 1: create entry
      const { data: entry } = await api.post("/progress/entries", { notes: notes.trim() || null });
      // Step 2: upload photos sequentially
      for (const p of photos) {
        await api.post(`/progress/entries/${entry.id}/photo`, { data: p.dataUrl });
      }
      // Step 3: upload video
      if (video) {
        await api.post(`/progress/entries/${entry.id}/video`, { data: video.dataUrl });
      }
      toast.success("Progress entry saved");
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="progress-new-entry-modal">
      <div className="bg-zinc-950 border border-zinc-800 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <header className="p-6 border-b border-zinc-800 flex items-center justify-between gap-4">
          <div>
            <p className="tracker text-zinc-500 text-[10px]">[ NEW PROGRESS ENTRY ]</p>
            <h2 className="font-display text-2xl">This week's check-in</h2>
          </div>
          <button type="button" onClick={onClose} className="text-zinc-400 hover:text-white"><X size={20} weight="bold" /></button>
        </header>
        <div className="p-6 space-y-6">
          <div>
            <label className="tracker text-zinc-400 text-[10px] block mb-2">Photos (up to 4 — front/side/back work well)</label>
            <div className="grid grid-cols-4 gap-2">
              {photos.map((p, i) => (
                <div key={i} className="relative aspect-square border border-zinc-800 bg-zinc-900 overflow-hidden group">
                  <img src={p.dataUrl} alt={p.name} className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => setPhotos((arr) => arr.filter((_, idx) => idx !== i))}
                    className="absolute top-1 right-1 p-1 bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`progress-new-photo-remove-${i}`}
                  ><Trash size={12} weight="bold" /></button>
                </div>
              ))}
              {photos.length < 4 && (
                <button
                  type="button"
                  onClick={() => photoInputRef.current?.click()}
                  className="aspect-square border border-dashed border-zinc-800 hover:border-zinc-700 flex items-center justify-center text-zinc-500 hover:text-zinc-300 transition-colors"
                  data-testid="progress-new-add-photo-btn"
                ><Plus size={20} weight="bold" /></button>
              )}
            </div>
            <input ref={photoInputRef} type="file" accept="image/*" multiple onChange={onPickPhotos} className="hidden" data-testid="progress-new-photo-input" />
            <p className="text-[10px] text-zinc-500 mt-2">Photos are auto-compressed to ≤2MB each (~1600px wide JPEG).</p>
          </div>

          <div>
            <label className="tracker text-zinc-400 text-[10px] block mb-2">Form-check video (optional, ≤10MB)</label>
            {video ? (
              <div className="border border-zinc-800 bg-zinc-900 p-3 flex items-center justify-between gap-3" data-testid="progress-new-video-preview">
                <div className="flex items-center gap-2 min-w-0">
                  <VideoCamera size={18} weight="duotone" className="text-brand-light shrink-0" />
                  <span className="text-zinc-300 text-sm truncate">{video.name}</span>
                  <span className="text-zinc-500 text-xs whitespace-nowrap">{(video.size / 1_000_000).toFixed(1)}MB</span>
                </div>
                <button type="button" onClick={() => setVideo(null)} className="text-zinc-400 hover:text-red-400" data-testid="progress-new-video-remove"><Trash size={14} weight="bold" /></button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => videoInputRef.current?.click()}
                className="w-full border border-dashed border-zinc-800 hover:border-zinc-700 py-6 flex flex-col items-center text-zinc-500 hover:text-zinc-300 transition-colors"
                data-testid="progress-new-add-video-btn"
              >
                <VideoCamera size={28} weight="duotone" className="mb-2" />
                <span className="text-sm">Click to pick a video</span>
                <span className="tracker text-[10px] mt-1">MP4, MOV, WEBM · ≤10MB · short clips work best</span>
              </button>
            )}
            <input ref={videoInputRef} type="file" accept="video/*" onChange={onPickVideo} className="hidden" data-testid="progress-new-video-input" />
          </div>

          <div>
            <label className="tracker text-zinc-400 text-[10px] block mb-2">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={1000}
              rows={3}
              placeholder="Bodyweight, how the week went, anything for your coach to see…"
              className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-zinc-600 resize-none"
              data-testid="progress-new-notes-input"
            />
            <p className="text-[10px] text-zinc-500 mt-1">{notes.length}/1000</p>
          </div>
        </div>
        <footer className="p-6 border-t border-zinc-800 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} disabled={saving} className="btn-ghost !py-2 !px-4 !text-xs disabled:opacity-50" data-testid="progress-new-cancel-btn">Cancel</button>
          <button
            type="button"
            onClick={save}
            disabled={saving || (!photos.length && !video)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-brand-light text-zinc-950 font-display text-xs tracking-wide hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="progress-new-save-btn"
          >
            <FloppyDisk size={14} weight="fill" /> {saving ? "Saving…" : "Save entry"}
          </button>
        </footer>
      </div>
    </div>
  );
}

function EntryDetailModal({ entryId, entry, onClose, onDeleted, onReload }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [posting, setPosting] = useState(false);

  const deleteEntry = async () => {
    if (!window.confirm("Delete this progress entry? This can't be undone.")) return;
    setBusy(true);
    try {
      await api.delete(`/progress/entries/${entryId}`);
      toast.success("Entry deleted");
      onDeleted();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const deletePhoto = async (i) => {
    setBusy(true);
    try {
      await api.delete(`/progress/entries/${entryId}/photo/${i}`);
      onReload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const deleteVideo = async () => {
    setBusy(true);
    try {
      await api.delete(`/progress/entries/${entryId}/video`);
      onReload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const postReply = async (e) => {
    e?.preventDefault?.();
    const text = replyText.trim();
    if (!text) {
      toast.error("Reply cannot be empty");
      return;
    }
    if (text.length > 1000) {
      toast.error("Reply too long (max 1000 chars)");
      return;
    }
    setPosting(true);
    try {
      await api.post(`/progress/entries/${entryId}/comment`, { text });
      setReplyText("");
      onReload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to post reply");
    } finally {
      setPosting(false);
    }
  };

  const deleteComment = async (id) => {
    if (!window.confirm("Delete your reply?")) return;
    setBusy(true);
    try {
      await api.delete(`/progress/entries/${entryId}/comment/${id}`);
      onReload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="progress-entry-modal">
      <div className="bg-zinc-950 border border-zinc-800 max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <header className="p-6 border-b border-zinc-800 flex items-center justify-between gap-4">
          <div>
            <p className="tracker text-zinc-500 text-[10px]">{entry?.week_label || "—"}</p>
            <h2 className="font-display text-2xl">{entry ? formatDate(entry.created_at) : "Loading…"}</h2>
          </div>
          <button type="button" onClick={onClose} className="text-zinc-400 hover:text-white"><X size={20} weight="bold" /></button>
        </header>
        {!entry ? (
          <div className="p-6 text-center tracker text-zinc-500 text-[11px]">Loading entry…</div>
        ) : (
          <div className="p-6 space-y-6">
            {entry.photos?.length > 0 && (
              <div>
                <p className="tracker text-zinc-500 text-[10px] mb-3">[ PHOTOS ]</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {entry.photos.map((src, i) => (
                    <div key={i} className="relative aspect-square border border-zinc-800 bg-zinc-900 overflow-hidden group" data-testid={`progress-entry-photo-${i}`}>
                      <img src={src} alt={`Progress ${i + 1}`} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => deletePhoto(i)}
                        disabled={busy}
                        className="absolute top-1 right-1 p-1 bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                        data-testid={`progress-entry-photo-remove-${i}`}
                      ><Trash size={12} weight="bold" /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {entry.video && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="tracker text-zinc-500 text-[10px]">[ FORM-CHECK VIDEO ]</p>
                  <button
                    type="button"
                    onClick={deleteVideo}
                    disabled={busy}
                    className="text-zinc-500 hover:text-red-400 text-xs inline-flex items-center gap-1 disabled:opacity-50"
                    data-testid="progress-entry-video-remove"
                  ><Trash size={12} weight="bold" /> Remove video</button>
                </div>
                <video
                  src={entry.video}
                  controls
                  className="w-full max-h-[480px] bg-zinc-900 border border-zinc-800"
                  data-testid="progress-entry-video-player"
                />
              </div>
            )}
            {entry.notes && (
              <div>
                <p className="tracker text-zinc-500 text-[10px] mb-2">[ NOTES ]</p>
                <p className="text-zinc-300 text-sm whitespace-pre-wrap">{entry.notes}</p>
              </div>
            )}
            <div className="pt-2 border-t border-zinc-800" data-testid="client-progress-thread-section">
              <p className="tracker text-zinc-500 text-[10px] mb-3 flex items-center gap-2">
                <ChatCircleText size={12} weight="bold" /> [ COACHING THREAD ]
              </p>
              {(entry.comments || []).length === 0 ? (
                <p className="text-zinc-500 text-xs italic mb-4">No messages yet — once your coach reviews this entry, their notes appear here.</p>
              ) : (
                <ul className="space-y-3 mb-4" data-testid="client-progress-comments-list">
                  {entry.comments.map((c) => {
                    const isCoach = c.author_role !== "client";
                    const isMine = c.author_id === user?.id;
                    return (
                      <li
                        key={c.id}
                        className={`border-l-2 pl-3 py-1 ${isCoach ? "border-brand-light" : "border-emerald-500/60"}`}
                        data-testid={`client-progress-comment-${c.id}`}
                      >
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className={`tracker text-[10px] ${isCoach ? "text-brand-light" : "text-emerald-400"}`}>
                            {isCoach ? c.author_name : `${c.author_name} (you)`}
                          </span>
                          <span className="tracker text-[10px] text-zinc-600">{new Date(c.created_at).toLocaleDateString()}</span>
                        </div>
                        <p className="text-zinc-200 text-sm whitespace-pre-wrap">{c.text}</p>
                        {isMine && (
                          <button
                            type="button"
                            onClick={() => deleteComment(c.id)}
                            disabled={busy}
                            className="mt-1.5 text-zinc-500 hover:text-red-400 text-[10px] tracker inline-flex items-center gap-1 disabled:opacity-50"
                            data-testid={`client-progress-comment-delete-${c.id}`}
                          >
                            <Trash size={10} weight="bold" /> Delete
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
              <form onSubmit={postReply} className="space-y-2" data-testid="client-progress-reply-form">
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  maxLength={1000}
                  rows={3}
                  placeholder="Reply to your coach — questions, how the week felt, anything…"
                  className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm focus:outline-none focus:border-emerald-500/50 resize-none rounded-sm"
                  data-testid="client-progress-reply-input"
                />
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] text-zinc-600">{replyText.length}/1000</span>
                  <button
                    type="submit"
                    disabled={posting || !replyText.trim()}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-zinc-950 font-display text-xs tracking-wide hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="client-progress-reply-submit"
                  >
                    <PaperPlaneTilt size={12} weight="fill" /> {posting ? "Posting…" : "Send reply"}
                  </button>
                </div>
              </form>
            </div>
            {!entry.photos?.length && !entry.video && !entry.notes && (
              <p className="text-zinc-500 text-sm text-center py-8">This entry is empty. Add photos or a video, or delete it.</p>
            )}
          </div>
        )}
        <footer className="p-6 border-t border-zinc-800 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={deleteEntry}
            disabled={busy}
            className="inline-flex items-center gap-2 text-red-400 hover:text-red-300 text-xs tracker disabled:opacity-50"
            data-testid="progress-entry-delete-btn"
          ><Trash size={14} weight="bold" /> Delete entry</button>
        </footer>
      </div>
    </div>
  );
}
